import React, { useState, useEffect } from 'react';
import { useConfig } from '../config/ConfigContext';
import '../styles/videoWatchPage.css'; // For slideUp/slideDown animations
import '../styles/shareModalToast.css';

const FloatingShareButton = ({ pageTitle, pageDescription, imageUrl }) => {
  const { theme } = useConfig();
  const [isMobile, setIsMobile] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const showToast = (message, isError = false) => {
    const toast = document.createElement('div');
    toast.className = 'share-toast';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background-color: ${isError ? '#f44336' : '#333'};
      color: white;
      padding: 12px 24px;
      border-radius: 4px;
      z-index: 9999;
      animation: slideUp 0.3s ease-out;
    `;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.animation = 'slideDown 0.3s ease-out';
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 2000);
  };

  const handleShare = async () => {
    if (isSharing) return;
    setIsSharing(true);

    const shareUrl = window.location.href;
    const shareTitle = pageTitle || document.title;
    const shareText = pageDescription
      ? `*${shareTitle}*\n\n${pageDescription}`
      : `*${shareTitle}*`;

    const slugify = (s) =>
      (s || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

    const getShareImageFile = async () => {
      if (!imageUrl) return null;
      try {
        const imgUrl = new URL(imageUrl, window.location.origin);
        const sameOrigin = imgUrl.origin === window.location.origin;
        const proxyUrl = sameOrigin
          ? imgUrl.toString()
          : `${window.location.origin}/.netlify/functions/image-proxy?url=${encodeURIComponent(
              imgUrl.toString()
            )}&filename=${encodeURIComponent(slugify(pageTitle) || 'shared-image')}`;

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        const res = await fetch(proxyUrl, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          signal: controller.signal,
        });
        clearTimeout(timeout);
        if (!res.ok) return null;

        const blob = await res.blob();
        const type = blob.type || 'image/jpeg';
        const ext = type.includes('png')
          ? 'png'
          : type.includes('webp')
          ? 'webp'
          : type.includes('gif')
          ? 'gif'
          : 'jpg';
        const filename = `${slugify(pageTitle) || 'shared-image'}.${ext}`;
        return new File([blob], filename, { type });
      } catch (e) {
        console.warn('FloatingShareButton image fetch failed:', e);
        return null;
      }
    };

    try {
      if (navigator.share) {
        let didShareWithFile = false;

        if (imageUrl && navigator.canShare) {
          const imageFile = await getShareImageFile();
          if (imageFile && navigator.canShare({ files: [imageFile] })) {
            await navigator.share({
              title: shareTitle,
              text: shareText,
              files: [imageFile],
              url: shareUrl,
            });
            didShareWithFile = true;
          }
        }

        if (!didShareWithFile) {
          await navigator.share({
            title: shareTitle,
            text: shareText,
            url: shareUrl,
          });
        }
        showToast(didShareWithFile ? 'Shared with image!' : 'Shared successfully!');
      } else {
        await navigator.clipboard.writeText(shareUrl);
        showToast('Link copied to clipboard!');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
        showToast('Failed to share. Please try again.', true);
      }
    } finally {
      setIsSharing(false);
    }
  };

  if (!isMobile) return null;

  return (
    <button
      onClick={handleShare}
      disabled={isSharing}
      style={{
        position: 'fixed',
        bottom: '80px',
        right: '16px',
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        backgroundColor: theme?.colors?.primary || '#1976d2',
        color: 'white',
        border: 'none',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1px',
        padding: '6px',
        zIndex: 1000,
        transition: 'all 0.3s ease',
        opacity: isSharing ? 0.7 : 1,
      }}
      onMouseEnter={(e) => {
        if (!isSharing) {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.3)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
      }}
      aria-label="Share this page"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="6" cy="12" r="2" stroke="currentColor" strokeWidth="2"/>
        <circle cx="18" cy="6" r="2" stroke="currentColor" strokeWidth="2"/>
        <circle cx="18" cy="18" r="2" stroke="currentColor" strokeWidth="2"/>
        <path d="M8 11L16 7M8 13L16 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
      <span style={{
        fontSize: '9px',
        fontWeight: '600',
        lineHeight: 1,
        marginTop: '1px'
      }}>
        Share
      </span>
    </button>
  );
};

export default FloatingShareButton;